import type { Mission } from "@/services/clientDataService";

/** La mission est-elle un regroupement (contient des sous-missions) ? */
export function isGroupMission(m: Mission): boolean {
  return m.grouping_mode === "group";
}

/** La mission est-elle une sous-mission (a un parent) ? */
export function isSubMission(m: Mission): boolean {
  return !!m.parent_mission_id;
}

/** Retourne les sous-missions d'une mission groupe. */
export function getSubMissions(m: Mission): Mission[] {
  return m.sub_missions ?? [];
}

/** Toutes les sous-missions du groupe sont-elles sélectionnées ? */
export function areAllSubMissionsSelected(
  mission: Mission,
  selectedIds: Set<string>
): boolean {
  const subs = getSubMissions(mission);
  if (subs.length === 0) return false;
  return subs.every((s) => selectedIds.has(s.id));
}

/** Au moins une sous-mission est sélectionnée, mais pas toutes. */
export function isSomeSubMissionsSelected(
  mission: Mission,
  selectedIds: Set<string>
): boolean {
  const subs = getSubMissions(mission);
  if (subs.length === 0) return false;
  const selectedCount = subs.filter((s) => selectedIds.has(s.id)).length;
  return selectedCount > 0 && selectedCount < subs.length;
}

/** Nombre de sous-missions sélectionnées. */
export function getSelectedSubMissionsCount(
  mission: Mission,
  selectedIds: Set<string>
): number {
  return getSubMissions(mission).filter((s) => selectedIds.has(s.id)).length;
}

/**
 * Extraire tous les IDs de missions (top-level + sous-missions) pour le cleanup.
 */
export function getAllMissionIds(missions: Mission[]): string[] {
  const ids: string[] = [];
  for (const m of missions) {
    ids.push(m.id);
    for (const sub of getSubMissions(m)) {
      ids.push(sub.id);
    }
  }
  return ids;
}

/**
 * Construit une Map<id, Mission> incluant les sous-missions pour des lookups O(1).
 * Résout le problème : allMissions.find(m => m.id === subId) retourne undefined
 * car les sous-missions sont imbriquées dans sub_missions[] et pas au top-level.
 */
export function buildFlatMissionMap(missions: Mission[]): Map<string, Mission> {
  const map = new Map<string, Mission>();
  for (const m of missions) {
    map.set(m.id, m);
    for (const sub of getSubMissions(m)) {
      map.set(sub.id, sub);
    }
  }
  return map;
}

/**
 * Déduplique selectedItems : si un parent groupe ET au moins une de ses sous-missions
 * sont tous les deux dans la sélection, retire le parent pour éviter le double comptage.
 *
 * Cas d'usage : données historiques où le parent a été ajouté avant la mise en place
 * du grouping (l'invariant useGroupMissionSelection dit que le parent ne devrait
 * JAMAIS être dans selectedItems, mais les données existantes peuvent le contenir).
 */
export function deduplicateSelectedMissionItems<
  T extends { id: string; type: string }
>(selectedItems: T[], allMissions: Mission[]): T[] {
  const selectedMissionIds = new Set(
    selectedItems.filter((i) => i.type === "mission").map((i) => i.id)
  );

  const parentIdsToRemove = new Set<string>();
  for (const m of allMissions) {
    if (!isGroupMission(m)) continue;
    const subs = getSubMissions(m);
    const hasSelectedSubs = subs.some((s) => selectedMissionIds.has(s.id));
    if (hasSelectedSubs && selectedMissionIds.has(m.id)) {
      parentIdsToRemove.add(m.id);
    }
  }

  if (parentIdsToRemove.size === 0) return selectedItems;

  return selectedItems.filter(
    (item) => item.type !== "mission" || !parentIdsToRemove.has(item.id)
  );
}

/**
 * Compte les missions "logiques" visibles par l'utilisateur.
 * Standalone sélectionnée = 1, groupe parent avec ≥1 sous-mission sélectionnée = 1.
 * Cohérent avec l'affichage À la carte qui montre les parents comme lignes uniques.
 */
export function countLogicalMissions(
  selectedItems: Array<{ id: string; type: string }>,
  allMissions: Mission[]
): number {
  const selectedMissionIds = new Set(
    selectedItems.filter((i) => i.type === "mission").map((i) => i.id)
  );
  if (selectedMissionIds.size === 0) return 0;

  const subMissionIds = new Set<string>();
  const countedParentIds = new Set<string>();

  for (const m of allMissions) {
    if (!isGroupMission(m)) continue;
    for (const sub of getSubMissions(m)) {
      subMissionIds.add(sub.id);
      if (selectedMissionIds.has(sub.id)) {
        countedParentIds.add(m.id);
      }
    }
  }

  // Standalone = sélectionnés qui ne sont ni sous-missions ni parents déjà comptés
  let standalone = 0;
  for (const id of selectedMissionIds) {
    if (!subMissionIds.has(id) && !countedParentIds.has(id)) {
      standalone++;
    }
  }

  return standalone + countedParentIds.size;
}

/**
 * Retourne une liste plate de toutes les missions (top-level + sous-missions).
 */
export function flattenMissions(missions: Mission[]): Mission[] {
  const result: Mission[] = [];
  for (const m of missions) {
    result.push(m);
    for (const sub of getSubMissions(m)) {
      result.push(sub);
    }
  }
  return result;
}
